import type { TFunction } from "i18next";
import type { ColumnsType } from "antd/es/table";
import { Switch } from "antd";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import type { User } from "../Types/UserTypes";
import { IsUserLocked } from "../Utils/UserPermissions";
import UserActionIcons from "./UserActionIcons";

interface UserTableColumnsParams {
  t: TFunction;
  isArabic: boolean;
  deletingUserId: number | null;
  onAccessLevelChange: (_userId: number, _accessLevel: string) => void;
  onToggleStatus: (_userId: number) => void;
  onEditUser: (_user: User) => void;
  onDeleteUser: (_user: User) => void;
}

const capitalize = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "";

export const getUserTableColumns = ({
  t,
  isArabic,
  deletingUserId,
  onAccessLevelChange,
  onToggleStatus,
  onEditUser,
  onDeleteUser,
}: UserTableColumnsParams): ColumnsType<User> => {
  const align = isArabic ? "center" : undefined;

  return [
    {
      title: t("userManagement.table.name"),
      dataIndex: "name",
      key: "name",
      align,
      render: (text: string) => <span className="campaign-name">{text}</span>,
    },
    {
      title: t("userManagement.table.email"),
      dataIndex: "email",
      key: "email",
      align,
    },
    {
      title: t("userManagement.table.accessLevel"),
      dataIndex: "accessLevel",
      key: "accessLevel",
      align,
      render: (_, user) => {
        const isLocked = IsUserLocked(user);

        return (
          <div className="access-level-cell-inner">
            <StyledDropdown
              value={capitalize(user.accessLevel)}
              showSearch={false}
              popupClassName="custom-dropdown-menu"
              placement="bottomLeft"
              onChange={value => !isLocked && onAccessLevelChange(user.id, value as string)}
              disabled={isLocked}
              className={`access-level-select ${isLocked ? "access-level-select--disabled" : ""}`}
              options={[
                { label: t("userManagement.role.viewer"), value: "Viewer" },
                { label: t("userManagement.role.admin"), value: "Admin" },
              ]}
            />
          </div>
        );
      },
    },
    {
      title: t("userManagement.table.status"),
      dataIndex: "isActive",
      key: "status",
      align,
      render: (_, user) => (
        <div>
          <Switch
            className="user-management-switch"
            checked={user.isActive}
            onChange={() => onToggleStatus(user.id)}
            disabled={IsUserLocked(user)}
          />
          <span style={isArabic ? { marginRight: 8 } : { marginLeft: 8 }}>
            {user.isActive ? t("userManagement.status.active") : t("userManagement.status.inactive")}
          </span>
        </div>
      ),
    },
    {
      title: t("userManagement.table.actions"),
      key: "actions",
      align,
      render: (_, user) => (
        <UserActionIcons
          user={user}
          isDisabled={IsUserLocked(user)}
          isDeleting={deletingUserId === user.id}
          isArabic={isArabic}
          onEdit={onEditUser}
          onDelete={onDeleteUser}
        />
      ),
    },
  ];
};
